import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch, usePermissions } from "@yk/frontend-core";
import { useDebouncedValue, type CommandPaletteItem } from "@yk/ui";
import { ClipboardList, UserCog } from "@yk/ui/icons";
import { useNavItems } from "../lib/nav.js";
import {
  buildComplaintQuery,
  type ComplaintListParams,
} from "../features/complaints/api.js";
import type {
  ComplaintRow,
  ListResponse,
} from "../features/complaints/types.js";
import { toCustomerRow, type RawTenant } from "../features/customers/format.js";
import { ROUTE_PERMISSIONS } from "../lib/route-table.js";

const MIN_CHARS = 2;
const RESULT_LIMIT = 6;

/**
 * Powers the topbar ⌘K palette: navigation commands plus live, debounced,
 * permission-aware search of complaints (by code/title) and customers (by
 * name/email). Entity queries only fire once the user types ≥2 chars.
 */
export function useGlobalSearch(): {
  query: string;
  setQuery: (q: string) => void;
  items: CommandPaletteItem[];
  loading: boolean;
} {
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { can } = usePermissions();
  const [query, setQuery] = useState("");
  const term = useDebouncedValue(query.trim(), 200);
  const active = term.length >= MIN_CHARS;

  const canComplaints = can(ROUTE_PERMISSIONS["/complaints"]);
  const canCustomers = can(ROUTE_PERMISSIONS["/customers"]);

  const complaints = useQuery({
    queryKey: ["search", "complaints", term],
    enabled: active && canComplaints,
    queryFn: async () => {
      const qs = buildComplaintQuery({
        search: term,
        limit: RESULT_LIMIT,
      } as ComplaintListParams);
      return (await apiFetch(
        `/api/v1/co/maintenance/work-orders?${qs}`,
      )) as ListResponse<ComplaintRow>;
    },
  });

  const customers = useQuery({
    queryKey: ["search", "customers", term],
    enabled: active && canCustomers,
    queryFn: async () => {
      const qs = new URLSearchParams({
        pageSize: String(RESULT_LIMIT),
        search: term,
      });
      const res = (await apiFetch(`/api/v1/co/tenants?${qs.toString()}`)) as {
        data: { rows: RawTenant[] };
      };
      return res.data.rows.map(toCustomerRow);
    },
  });

  const items = useMemo<CommandPaletteItem[]>(() => {
    const out: CommandPaletteItem[] = [];

    // Navigation commands — always available; filtered client-side by the query.
    const q = query.trim().toLowerCase();
    for (const n of navItems) {
      if (q && !n.label.toLowerCase().includes(q)) continue;
      out.push({
        id: `nav-${n.to}`,
        group: "Go to",
        label: n.label,
        ...(n.icon ? { icon: n.icon } : {}),
        onSelect: () => void navigate(n.to),
      });
    }

    if (active) {
      for (const c of complaints.data?.data.rows ?? []) {
        out.push({
          id: `complaint-${c.id}`,
          group: "Complaints",
          label: `${c.number} — ${c.title}`,
          icon: ClipboardList,
          onSelect: () =>
            void navigate(`/complaints/${c.id}`),
        });
      }
      for (const c of customers.data ?? []) {
        out.push({
          id: `customer-${c.id}`,
          group: "Customers",
          label: c.displayName,
          icon: UserCog,
          onSelect: () =>
            void navigate(`/customers/${c.id}`),
        });
      }
    }
    return out;
  }, [query, navItems, active, complaints.data, customers.data, navigate]);

  const loading =
    active &&
    ((canComplaints && complaints.isFetching) ||
      (canCustomers && customers.isFetching));

  return { query, setQuery, items, loading };
}
